import {
  IAssetGroupQueryParams,
  INotificationHistoryParam,
  ITradeInDevicesBrandParams,
  IVerifyResetPasswordTokenParams,
} from './types';

/**
 * -----------------------------------------------------------------------------
 * These services provide user authentication and authorization stateful logic.
 * -----------------------------------------------------------------------------
 */

/**
 * Main `identity service` that makes login requests and provides the necessary
 * `user` info, `token` and initial `refreshToken` to be used across other services.
 * - `POST`
 */
export const loginUrl = () => '/identity/member/login';

/**
 * Main `identity service` that makes login type requests and provides the
 * necessary authentication type to be used in login services.
 * - `POST`
 */
export const loginTypeUrl = () => '/user/authentication/type';

/**
 * Endpoint to call when user wants to login using OTP.
 */
export const loginTypeOtpUrl = () => '/identity/otp';

/**
 * Url to verify the OTP.
 */
export const verifyOtpUrl = () => '/identity/verifyOtp';

/**
 * This `identity service` endpoint verifies the `otp` sent to the user's email.
 */
export const validateEmailLoginOTPUrl = () => '/identity/verifyOtp';

/**
 * This `identity service` endpoint verifies the `otp` sent to the user's mobile.
 */
export const validateLoginOTPUrl = () => '/identity/dealer-verify-otp';

/**
 * Url to validate the SSO endpoint.
 */
export const validateSSOUrl = () => '/user/authentication/validate';

/**
 * This `identity service` endpoint sends the `otp` sent to the user's mobile.
 */
export const sendLoginOTPUrl = () => '/identity/dealer-otp';

/**
 * This `identity service` endpoint sends the `otp` sent to the user's email.
 */
export const sendEmailLoginOTPUrl = () => '/identity/otp';

/**
 * This `identity service` generates the `refreshToken` if expired.
 */
export const refreshTokenUrl = () => '/identity/token';

/**
 * Makes logout requests.
 */
export const logoutUrl = () => '/user/member/logout';

/**
 * Url for admin login using email-id and password.
 */
export const adminPasswordLoginUrl = () => '/user/authentication/admin/login';

/**
 * Url to logout the admin user.
 */
export const adminLogoutUrl = () => '/identity/logout';

/**
 * This makes profile update requests.
 */
export const profileUpdateUrl = () => '/user/update-profile';

/**
 * This `identity service` endpoint sends the `otp` sent to the user's email when
 * the user has forgotten their password.
 */
export const sendForgotPasswordOTPUrl = () => '/identity/forgot-password';

/**
 * This `identity service` endpoint allows user to enter the `otp` and reset
 * their password.
 */
export const resetPasswordUrl = () => '/identity/reset-password';

/**
 * This `identity service` endpoint allows user to change their password from the
 * profile screen.
 */
export const changePasswordUrl = () => '/identity/change-password';

/**
 * This `identity service` endpoint for fetching notification history
 */
export const fetchNotificationHistoryUrl = ({
  count,
  lastDocumentId,
  readStatus,
  screen,
}: INotificationHistoryParam) => {
  const queryParams = new URLSearchParams();
  queryParams.append('count', count.toString());

  if (lastDocumentId) {
    queryParams.append('lastDocumentId', lastDocumentId);
  }

  if (readStatus) {
    queryParams.append('readStatus', readStatus);
  }

  if (screen) {
    queryParams.append('screen', screen);
  }

  return `/notifications/app/history?${queryParams.toString()}`;
};

/**
 * This `identity service` endpoint allows to update the viewed status of
 * notification history item
 */
export const notificationViewedUrl = (markAll?: boolean) =>
  markAll
    ? '/notifications/app/history/viewed/all'
    : '/notifications/app/history/viewed';

/**
 * This `identity service` endpoint for fetching notification unread boolean
 */
export const fetchUnreadNotificationUrl = () =>
  '/notifications/app/history/unread';

export const updateUserLanguageUrl = () => '/user/member/device-locale';

export const updateUserExpoNotificationTokenUrl = () =>
  '/user/notification/token';

// -----------------------------------------------------------------------------
// ------------------------ 📝 DEALER SIGN UP ENDPOINTS ------------------------
// -----------------------------------------------------------------------------

/**
 * Makes account deletion requests.
 */
export const accountDeleteUrl = () => '/dealer-signup/delete-account';

/**
 * This endpoint that sends the OTP required for user registration.
 */
export const sendUserRegistrationOTPUrl = () => '/dealer-signup/send-otp';

/**
 * This endpoint that validate/verifies the OTP required for user registration.
 */
export const validateUserRegistrationOTPUrl = () => '/dealer-signup/verify-otp';

/**
 * This endpoint updates the current registration details of the user based on
 */
export const updateUserRegistrationDetailsUrl = () => '/dealer-signup/update';

/**
 * This endpoint retrieves the current registration details of the user based on
 * the provided token.
 */
export const fetchUserRegistrationDetailsUrl = () => '/dealer-signup/details';

/**
 * This endpoint retrieves the category of devices users can trade in.
 */
export const fetchTradeInDeviceCategoriesUrl = (isPlatformUser?: boolean) => {
  if (isPlatformUser) {
    return '/paas/dealer/bidding-window/categories';
  }

  return '/store/categories';
};

/**
 * This endpoint retrieves the category of devices users can trade in.
 */
export const fetchPublicTradeInDeviceCategoriesUrl = (tenantId?: string) => {
  if (tenantId) {
    return `/category/electronics?tenantId=${tenantId}`;
  }

  return '/category/electronics';
};

/**
 * This endpoint retrieves the categories of devices buyers can purchase.
 * TODO: @bobin to remove this later
 */
export const fetchBuyerDeviceCategoriesUrl = () => '/admin/category/categories';

/**
 * This endpoint retrieves the brands of devices users can trade in.
 */
export const fetchTradeInDeviceBrandsUrl = ({
  categoryId,
}: ITradeInDevicesBrandParams) => `/store/brands/${categoryId}`;

/**
 * This endpoint retrieves the asset groups
 */
export const searchAssetGroupsUrl = ({ categoryId }: IAssetGroupQueryParams) =>
  `/store/asset-groups/${categoryId}`;

/**
 * Endpoint to trigger the email that contains reset password link
 */
export const triggerResetPasswordEmailUrl = () => '/user/forgot-password';

/**
 * Verify the reset token after user clicks on the reset url
 */
export const verifyResetPasswordTokenUrl = ({
  resetToken,
}: IVerifyResetPasswordTokenParams) =>
  `/user/validate-reset-token/${resetToken}`;

/**
 * Reset the user's password with the provided new password and reset token.
 */
export const resetPasswordWithTokenUrl = () => '/user/reset-password';

/**
 * Fetch password policy details.
 */
export const fetchPasswordPolicyUrl = () => '/user/password-policy';

/**
 * This endpoint retrieves the list of country codes based on the provided tenant.
 */
export const fetchTenantCountryListUrl = (tenantId: string) =>
  `/tenant/${tenantId}/countries`;

/**
 * This endpoint retrieves the asset groups for buyers.
 * Single brand: path only. Multiple brands: path placeholder + brandId query params.
 */
export const fetchBuyerAssetGroupsUrl = ({
  brandIds,
  categoryId,
}: {
  categoryId: string;
  brandIds: string[];
}) => {
  if (brandIds.length === 0) {
    return `/assets/groups/${categoryId}/0`;
  }

  if (brandIds.length === 1) {
    return `/assets/groups/${categoryId}/${brandIds[0]}`;
  }

  const params = new URLSearchParams();
  brandIds.forEach((id) => params.append('brandId', id));

  return `/assets/groups/${categoryId}/0?${params.toString()}`;
};
